import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'

import BoundsWatcher from '../../shared/utilities/dom/BoundsWatcher'
import ClickAnywhere from '../../shared/utilities/dom/ClickAnywhere'
import { setStylePrefixed } from '../../shared/utilities/styleUtils'

/**
 * Breakout
 *
 * To be used with popouts and dropdowns, when an element needs to temporarily break out
 * of a parent with overflow:hidden, but still appear to stay put in its original position.
 *
 * It will appear on top of the page in front of other elements, sort of like a modal.
 * Its position is determined by props.trackElement, which is monitored for changes in
 * position due to scrolling.
 *
 * It also provides close detection functionality which is throrough and in line with
 * WAI-ARIA recommendations, detecting click/focus outside or Esc key press.
 *
 * One inevitable implication is that children will be re-mounted, local state wiped.
 * That's React's portal default behaviour.
 *
 */
class Breakout extends React.Component {
  constructor(props) {
    super(props)

    this.state = { isInDOM: false }
    this.rootEl = document.body
    this.el = this.createElement()
    this.watcher = new BoundsWatcher()
    this.clickAnywhere = null
  }

  createElement() {
    const el = document.createElement('div')

    el.setAttribute('data-name', 'BREAKOUT')

    // Default base styles plus props.style
    const style = {
      position: 'absolute',
      left: 0,
      top: 0,
      zIndex: 100,
      outline: this.props.debug ? '1px dashed rgba(255,0,0,0.5)' : '',
      ...this.props.style
    }

    for (const i in style) el.style[i] = style[i]

    return el
  }

  /**
   * Important detail:
   * By this time children are created by React, but this.el is not present in the DOM yet.
   * So any child would fail trying to access its DOM elements in componentDidMount().
   * Hence we render twice, including children only on second pass when they're fully mounted.
   * @see React docs, it's intended behaviour.
   */
  componentDidMount() {
    if (!this.watcher.isValidElement(this.props.trackElement)) {
      console.error('<Breakout> Invalid props.trackElement', this.props.trackElement)
      return
    }

    this.rootEl.appendChild(this.el)

    // Sets the DOM flag, then the real action starts in componentDidUpdate()
    if (!this.state.isInDOM) {
      this.setState({ isInDOM: true })
    }
  }

  componentWillUnmount() {
    this.rootEl.removeChild(this.el)
    if (this.watcher) this.watcher.stop()
    if (this.clickAnywhere) this.clickAnywhere.stop()
  }

  componentDidUpdate() {
    if (this.state.isInDOM) {
      if (!this.watcher.isActive) {
        this.watcher.start(
          this.props.trackElement,
          (x, y) => {
            setStylePrefixed(this.el, {transform: `translate3d(${x}px,${y}px,0px)`})
          },
          (width, height) => {
            if (this.props.resize) {
              this.el.style.width = `${width}px`
              this.el.style.height = `${height}px`
            }
          }
        )
      }
      if (this.props.onClickOutside && !this.clickAnywhere) {
        this.clickAnywhere = new ClickAnywhere()
        this.clickAnywhere.except([ this.el, this.props.trackElement ]).toCall(this.props.onClickOutside)
      }
    }
  }

  render() {
    return ReactDOM.createPortal(
      this.state.isInDOM ? this.props.children : null,
      this.el
    )
  }
}

Breakout.propTypes = {
  trackElement: PropTypes.instanceOf(Element).isRequired,
  onClickOutside: PropTypes.func,
  resize: PropTypes.bool,
  style: PropTypes.object,
  debug: PropTypes.bool
}
Breakout.defaultProps = {
  trackElement: null,
  onClickOutside: null,
  resize: true,
  style: null,
  debug: false
}

export default Breakout
